import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME } from "../../components/ProtectedRoute";
import { useTheme } from "../../context/ThemeContext";
import toast from "react-hot-toast";
import BRAND from "../../brand";

const FEATURES = [
  { icon: "👥", label: "Smart Queues", sub: "Intelligent Flow" },
  { icon: "🖥️", label: "Live Displays", sub: "Real-time Updates" },
  { icon: "📅", label: "Appointments", sub: "Seamless Scheduling" },
  { icon: "😊", label: "Happier Customers", sub: "Better Experiences" },
];

export default function LoginPage() {
  const { login }    = useAuth();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const styles = getStyles(isDark);

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const user = await login(email.trim(), password);

      if (user.mustChangePassword) {
        navigate("/change-password", { replace: true });
        return;
      }

      const from = location.state?.from?.pathname;
      const home = ROLE_HOME[user.role] || "/login";
      navigate(from || home, { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail
        || err.response?.data?.non_field_errors?.[0]
        || "Invalid email or password.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.root}>
      {BRAND.loginStyle === "bands" ? <BackgroundBands isDark={isDark} /> : <BackgroundWaves isDark={isDark} />}

      <button
        type="button"
        onClick={toggleTheme}
        style={styles.themeToggle}
        title={isDark ? "Switch to light theme" : "Switch to dark theme"}
      >
        {isDark ? "☀️" : "🌙"}
      </button>

      <div style={styles.content}>
        {/* Logo lockup */}
        <div style={styles.logoWrap}>
          {BRAND.logoFull ? (
            <img
              src={`${process.env.PUBLIC_URL}/${BRAND.logoFull}`}
              alt={BRAND.name}
              style={BRAND.loginStyle === "bands"
                ? { width: "min(260px, 70vw)", height: "auto", display: "block", background: "#FFFFFF", borderRadius: "18px", padding: "18px 24px", boxShadow: "0 10px 30px rgba(0,0,0,0.18)" }
                : { width: "min(300px, 80vw)", height: "auto", display: "block" }}
            />
          ) : (<>
          <div style={styles.logoMark}><svg width="88" height="88" viewBox="0 0 32 32" fill="none" style={{ display: "block" }}>
              <rect width="32" height="32" rx="8" fill="#408DC9"/>
              <path d="M8 16C8 11.58 11.58 8 16 8V12C13.79 12 12 13.79 12 16C12 18.21 13.79 20 16 20V24C11.58 24 8 20.42 8 16Z" fill="white"/>
              <path d="M16 8C20.42 8 24 11.58 24 16H20C20 13.79 18.21 12 16 12V8Z" fill="#B3D3EC"/>
              <circle cx="20" cy="20" r="3" fill="#D5E7F5"/>
            </svg></div>
          <div style={styles.logoTextWrap}>
            <div style={styles.logoText}>
              <span style={{ color: isDark ? "#FFFFFF" : "#0F1E3D" }}>{BRAND.lead}</span>
              <span style={{ color: "#408DC9" }}>{BRAND.tail}</span>
            </div>
            <div style={styles.tagline}>
              <span style={styles.taglineLine} />
              {BRAND.tagline}
              <span style={styles.taglineLine} />
            </div>
          </div>
          </>)}
        </div>

        <div style={styles.card}>
          <h1 style={styles.title}>Welcome back</h1>
          <p style={styles.subtitle}>Sign in to your account to continue</p>
          <div style={styles.diamondDivider}>
            <span style={styles.diamondLine} />
            <span style={styles.diamond} />
            <span style={styles.diamondLine} />
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email address</label>
              <div style={styles.inputWrap}>
                <span style={styles.inputIcon}>✉️</span>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@hospital.com"
                  required
                  autoFocus
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.inputWrap}>
                <span style={styles.inputIcon}>🔒</span>
                <input
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{ ...styles.input, paddingRight: "44px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  style={styles.eyeBtn}
                  tabIndex={-1}
                >
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
              <div style={styles.forgotWrap}>
                <a href="/forgot-password" style={styles.forgotLink}>Forgot password?</a>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              style={{
                ...styles.submitBtn,
                opacity: (loading || !email || !password) ? 0.6 : 1,
              }}
            >
              {loading ? (
                <span style={styles.btnLoader}>
                  <span style={styles.spinDot} /> Signing in…
                </span>
              ) : (
                <span style={styles.btnLoader}>Sign in <span>→</span></span>
              )}
            </button>
          </form>

          {BRAND.sso && (<>
          <div style={styles.orDivider}>
            <span style={styles.orLine} />
            <span style={styles.orText}>OR</span>
            <span style={styles.orLine} />
          </div>

          <button
            type="button"
            onClick={() => toast("SSO isn't configured for this demo — sign in with email and password instead.")}
            style={styles.ssoBtn}
          >
            🛡️ Sign in with SSO
          </button>
          </>)}
        </div>

        {BRAND.loginFeatures && (
        <div style={styles.features}>
          {FEATURES.map((f) => (
            <div key={f.label} style={styles.featureItem}>
              <div style={styles.featureIconWrap}>{f.icon}</div>
              <div>
                <div style={styles.featureLabel}>{f.label}</div>
                <div style={styles.featureSub}>{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
        )}

        <div style={styles.pageFooter}>
          <div style={{ width: "100%" }}>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</div>
          <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
          <span style={styles.footerDot}>·</span>
          <a href="/terms" style={styles.footerLink}>Terms of Service</a>
        </div>
      </div>
    </div>
  );
}

function BackgroundWaves({ isDark }) {
  const blueFrom = isDark ? "#5A9FD4" : "#B3D3EC";
  const blueFromOpacity = isDark ? 0.7 : 0.55;
  const blueTo = "#408DC9";
  const blueToOpacity = isDark ? 0.35 : 0.15;
  const goldOpacity = isDark ? 0.9 : 0.6;

  return (
    <svg
      style={styles_shared.wavesSvg}
      viewBox="0 0 1440 1024"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="waveBlue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={blueFrom} stopOpacity={blueFromOpacity} />
          <stop offset="100%" stopColor={blueTo} stopOpacity={blueToOpacity} />
        </linearGradient>
        <linearGradient id="waveGold" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8CB802" stopOpacity={goldOpacity} />
          <stop offset="100%" stopColor="#BBDA63" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d="M-100,300 C300,150 500,450 900,250 C1200,100 1400,300 1540,180 L1540,-100 L-100,-100 Z"
            fill="url(#waveBlue)" />
      <path d="M-100,1124 C300,900 600,1050 950,850 C1250,680 1350,900 1540,780 L1540,1124 Z"
            fill="url(#waveBlue)" />
      <path d="M-50,820 C350,700 650,900 1000,760 C1300,640 1450,820 1550,760"
            stroke="url(#waveGold)" strokeWidth="4" fill="none" opacity="0.8" />
      <path d="M-50,120 C350,220 650,60 1000,160 C1250,240 1400,120 1550,180"
            stroke="url(#waveGold)" strokeWidth="3" fill="none" opacity="0.6" />
      {isDark && (
        <>
          <circle cx="220" cy="180" r="1.5" fill="#fff" opacity="0.6" />
          <circle cx="1280" cy="860" r="1.5" fill="#fff" opacity="0.5" />
          <circle cx="1180" cy="140" r="1" fill="#fff" opacity="0.4" />
          <circle cx="140" cy="900" r="1" fill="#fff" opacity="0.4" />
        </>
      )}
    </svg>
  );
}

// Straight-line concept: a solid brand-colour band behind the logo, diagonal
// pinstripes, a thin accent stripe, and a faint graph-paper grid below.
function BackgroundBands({ isDark }) {
  const primary = BRAND.colorPrimary;
  const accent = BRAND.colorAccent;
  const BAND = 270;
  return (
    <svg
      style={styles_shared.wavesSvg}
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id="bandStripes" width="26" height="26" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="26" stroke="#FFFFFF" strokeWidth="6" strokeOpacity="0.10" />
        </pattern>
        <pattern id="bandGrid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M48 0H0V48" fill="none" stroke={primary} strokeWidth="1" strokeOpacity={isDark ? 0.18 : 0.10} />
        </pattern>
      </defs>
      <rect x="0" y={BAND} width="100%" height="100%" fill="url(#bandGrid)" />
      <rect x="0" y="0" width="100%" height={BAND} fill={primary} />
      <rect x="0" y="0" width="100%" height={BAND} fill="url(#bandStripes)" />
      <rect x="0" y={BAND} width="100%" height="8" fill={accent} />
      <rect x="0" y="100%" width="100%" height="14" transform="translate(0,-14)" fill={primary} />
      <rect x="0" y="100%" width="100%" height="5" transform="translate(0,-19)" fill={accent} />
    </svg>
  );
}

const styles_shared = {
  wavesSvg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
  },
};

function getStyles(isDark) {
  return {
    root: {
      minHeight: "100vh",
      background: BRAND.loginStyle === "bands"
        ? (isDark ? "#0B2236" : "#FFFFFF")
        : isDark
        ? "radial-gradient(ellipse 80% 60% at 50% 0%, #0B1530 0%, #060D1F 60%, #030712 100%)"
        : "linear-gradient(160deg, #F0F6FF 0%, #E3EEFF 45%, #F5F9FF 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      position: "relative",
      overflow: "hidden",
      padding: "40px 20px",
    },
    wavesSvg: styles_shared.wavesSvg,
    themeToggle: {
      position: "absolute",
      top: "20px",
      right: "20px",
      zIndex: 2,
      width: "40px",
      height: "40px",
      borderRadius: "50%",
      border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #E2E8F0",
      background: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.8)",
      cursor: "pointer",
      fontSize: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(6px)",
    },
    content: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "560px",
    },
    logoWrap: {
      display: "flex",
      alignItems: "center",
      gap: "18px",
      marginBottom: "36px",
    },
    logoMark: {
      width: "88px",
      height: "88px",
      borderRadius: "16px",
      objectFit: "cover",
      filter: isDark
        ? "drop-shadow(0 0 24px rgba(90,159,212,0.55))"
        : "drop-shadow(0 8px 20px rgba(64,141,201,0.35))",
    },
    logoTextWrap: {
      display: "flex",
      flexDirection: "column",
    },
    logoText: {
      fontSize: "40px",
      fontWeight: "800",
      letterSpacing: "-1px",
      lineHeight: 1,
    },
    tagline: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginTop: "10px",
      fontSize: "12px",
      fontWeight: "600",
      letterSpacing: "3px",
      color: isDark ? "#A5CF2E" : "#6F9302",
    },
    taglineLine: {
      width: "18px",
      height: "1px",
      background: isDark ? "#A5CF2E" : "#6F9302",
      opacity: 0.6,
    },
    card: {
      background: isDark ? "rgba(15,23,42,0.82)" : "rgba(255,255,255,0.92)",
      border: isDark ? "1px solid rgba(140,184,2,0.35)" : "1px solid rgba(226,232,240,0.9)",
      borderRadius: "22px",
      padding: "44px 40px",
      width: "100%",
      boxShadow: isDark
        ? "0 25px 70px rgba(0,0,0,0.55), 0 0 40px rgba(64,141,201,0.15)"
        : "0 25px 60px rgba(64,141,201,0.12), 0 0 0 1px rgba(255,255,255,0.5) inset",
      backdropFilter: "blur(14px)",
    },
    title: {
      fontSize: "26px",
      fontWeight: "700",
      color: isDark ? "#F8FAFC" : "#0F1E3D",
      margin: "0 0 6px",
      textAlign: "center",
      letterSpacing: "-0.5px",
    },
    subtitle: {
      fontSize: "14px",
      color: isDark ? "#94A3B8" : "#64748B",
      margin: 0,
      textAlign: "center",
    },
    diamondDivider: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      margin: "18px 0 28px",
    },
    diamondLine: {
      flex: 1,
      height: "1px",
      background: isDark
        ? "linear-gradient(90deg, transparent, rgba(140,184,2,0.4))"
        : "linear-gradient(90deg, transparent, #CBD5E1)",
    },
    diamond: {
      width: "6px",
      height: "6px",
      background: "#8CB802",
      transform: "rotate(45deg)",
      display: "inline-block",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "18px",
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    label: {
      fontSize: "13px",
      fontWeight: "600",
      color: isDark ? "#CBD5E1" : "#334155",
    },
    inputWrap: {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    inputIcon: {
      position: "absolute",
      left: "14px",
      fontSize: "15px",
      opacity: 0.7,
    },
    input: {
      background: isDark ? "rgba(30,41,59,0.7)" : "#F8FAFC",
      border: isDark ? "1px solid rgba(51,65,85,0.9)" : "1px solid #E2E8F0",
      borderRadius: "10px",
      padding: "13px 14px 13px 40px",
      color: isDark ? "#F1F5F9" : "#0F1E3D",
      fontSize: "15px",
      outline: "none",
      width: "100%",
      boxSizing: "border-box",
      transition: "border-color 0.2s",
    },
    eyeBtn: {
      position: "absolute",
      right: "12px",
      background: "none",
      border: "none",
      cursor: "pointer",
      fontSize: "16px",
      opacity: 0.6,
      padding: "4px",
    },
    forgotWrap: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "2px",
    },
    forgotLink: {
      fontSize: "13px",
      color: isDark ? "#A5CF2E" : "#408DC9",
      textDecoration: "none",
      fontWeight: "500",
    },
    submitBtn: {
      background: "linear-gradient(135deg, #408DC9, #3478AD)",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      padding: "14px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "4px",
      boxShadow: "0 10px 24px rgba(64,141,201,0.35)",
      letterSpacing: "0.01em",
    },
    btnLoader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    spinDot: {
      width: "14px",
      height: "14px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      display: "inline-block",
      animation: "spin 0.7s linear infinite",
    },
    orDivider: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      margin: "26px 0 18px",
    },
    orLine: {
      flex: 1,
      height: "1px",
      background: isDark ? "rgba(148,163,184,0.25)" : "#E2E8F0",
    },
    orText: {
      fontSize: "12px",
      fontWeight: "600",
      color: isDark ? "#64748B" : "#94A3B8",
      letterSpacing: "0.05em",
    },
    ssoBtn: {
      width: "100%",
      background: isDark ? "rgba(30,41,59,0.5)" : "#fff",
      border: isDark ? "1px solid rgba(51,65,85,0.9)" : "1px solid #E2E8F0",
      borderRadius: "10px",
      padding: "13px",
      fontSize: "14px",
      fontWeight: "600",
      color: isDark ? "#E2E8F0" : "#408DC9",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
    },
    features: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "28px",
      marginTop: "40px",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    featureIconWrap: {
      width: "38px",
      height: "38px",
      borderRadius: "50%",
      background: isDark ? "rgba(140,184,2,0.12)" : "rgba(64,141,201,0.1)",
      border: isDark ? "1px solid rgba(90,159,212,0.4)" : "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "16px",
      flexShrink: 0,
    },
    featureLabel: {
      fontSize: "13px",
      fontWeight: "600",
      color: isDark ? "#F1F5F9" : "#0F1E3D",
    },
    featureSub: {
      fontSize: "11px",
      color: isDark ? "#64748B" : "#94A3B8",
    },
    pageFooter: {
      marginTop: BRAND.loginFeatures ? "36px" : "28px",
      fontSize: BRAND.loginFooter === "bold" ? "13px" : "12px",
      fontWeight: BRAND.loginFooter === "bold" ? "700" : "400",
      color: BRAND.loginFooter === "bold" ? (isDark ? "#CBD5E1" : "#1E293B") : (isDark ? "#475569" : "#94A3B8"),
      textAlign: "center",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      rowGap: "4px",
    },
    footerDot: {
      margin: "0 8px",
      color: BRAND.loginFooter === "bold" ? (isDark ? "#94A3B8" : "#475569") : (isDark ? "#334155" : "#CBD5E1"),
    },
    footerLink: {
      color: BRAND.loginFooter === "bold" ? (isDark ? "#E2E8F0" : "#0F1E3D") : (isDark ? "#64748B" : "#94A3B8"),
      fontWeight: BRAND.loginFooter === "bold" ? "700" : "400",
      textDecoration: "none",
      whiteSpace: "nowrap",
    },
  };
}
